import type { ReactNode } from 'react';
import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { PillButton } from '@sorare/core/src/atoms/buttons/PillButton';
import { Scarcities } from '@sorare/core/src/atoms/icons/Scarcities/index.tsx';
import { iconChevronLeft } from '@sorare/core/src/atoms/icons/system/iconChevronLeft.ts';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { EssenceBalance } from '@sorare/core/src/components/balance/EssenceBalance/index.tsx';
import { InGameCurrencyBalance } from '@sorare/core/src/components/balance/InGameCurrencyBalance/index.tsx';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser/index.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import {
  formatScarcity,
  playableBlockchainRarities,
} from '@sorare/core/src/lib/cards';
import { getRarityRGBColor } from '@sorare/core/src/lib/colors';
import { inGameCurrencyToRarity } from '@sorare/core/src/lib/scarcity.ts';

import { MissionPageHeader } from '#src/pages/PlayMissionsPageLayout/MissionPageHeader/index.tsx';

const Root = styled.div`
  position: relative;
  isolation: isolate;
`;

const StyledSBHorizontal = styled(SBHorizontal)`
  padding: var(--unit) 0;
`;

const BottomBar = styled.div`
  position: fixed;
  bottom: max(
    var(--double-unit),
    env(safe-area-inset-bottom, var(--double-unit))
  );
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
`;

type Props = {
  children: ReactNode;
  rarity: Rarity;
  categoryIcon: ReactNode;
  categoryTitle: ReactNode;
  goBack: () => void;
  onSelectRarity: (rarity: Rarity) => void;
  unclaimedTasksByRarity?: { [k in Rarity]?: number };
};

export const ProMissionsPageLayout = ({
  children,
  rarity,
  categoryIcon,
  categoryTitle,
  goBack,
  onSelectRarity,
  unclaimedTasksByRarity,
}: Props) => {
  const { sport } = useSportContext();
  const { lifecycle, update } = useLifecycle();
  const { currentUser } = useCurrentUserContext();
  const xpBalance = currentUser?.xpBalances?.find(
    ({ currency }) => inGameCurrencyToRarity[currency] === rarity
  );
  const shardsValue =
    currentUser?.cardShardsChests
      ?.filter(chest => chest.rarity === rarity)
      .reduce((sum, chest) => sum + chest.cardShardsCount, 0) ?? 0;

  return (
    <Root>
      <Container>
        <StyledSBHorizontal>
          <IconButton
            size="medium"
            color="quaternary"
            icon={iconChevronLeft}
            onClick={goBack}
          />
          <Horizontal>
            {!!xpBalance && (
              <InGameCurrencyBalance
                currency={xpBalance.currency}
                amount={
                  <FormattedNumber
                    value={xpBalance.amount}
                    notation="compact"
                  />
                }
              />
            )}
            <EssenceBalance rarity={rarity} value={shardsValue} />
          </Horizontal>
        </StyledSBHorizontal>
        <Vertical gap={5}>
          <MissionPageHeader
            categoryIcon={categoryIcon}
            categoryTitle={categoryTitle}
          />
          {children}
        </Vertical>
      </Container>
      <BottomBar>
        <Horizontal gap={1} center>
          {playableBlockchainRarities.map(R => (
            <Badge
              key={R}
              badgeContent={unclaimedTasksByRarity?.[R]}
              size="small"
            >
              <PillButton
                selected={R === rarity}
                icon={<Scarcities rarities={[R]} />}
                label={formatScarcity(R as Rarity)}
                colorRgb={getRarityRGBColor(R as Rarity)}
                viewTransitionName={`rarity-picker-${R}`}
                onClick={() => {
                  update(LIFECYCLE.missionsRarityFilter, {
                    ...lifecycle?.missionsRarityFilter,
                    [sport]: R,
                  });
                  onSelectRarity(R as Rarity);
                }}
              />
            </Badge>
          ))}
        </Horizontal>
      </BottomBar>
    </Root>
  );
};
